// ── B2B ─────────────────────────────────────────────────────────────────────
// Maps AI agent tool names to RTK Query tags that should be invalidated.
// Section-specific tags fire lightweight per-section endpoints instantly.
// BorrowerProfile tag triggers the heavier composite refetch (deferred).
export const AI_TOOL_TAG_MAP = {
  update_personal_details:     ['PersonalDetails', 'BorrowerProfile', 'getApplication', 'getHousingExpense'],
  update_borrower_address:     ['PersonalDetails', 'BorrowerProfile', 'getApplication'],
  update_loan_info:            ['LoanDetails', 'BorrowerProfile', 'TransactionDetails', 'getApplication'],
  update_employment_profiles:  ['EmploymentIncome', 'getApplication'],
  update_employment_records:   ['EmploymentIncome', 'getApplication'],
  update_borrower_assets:      ['Assets', 'TransactionDetails', 'getApplication'],
  update_borrower_liabilities: ['LiabilitiesLegacy', 'Liabilities', 'TransactionDetails', 'getApplication'],
  update_borrower_real_estate: ['RealEstate', 'BorrowerProfile', 'getHousingExpense', 'getApplication'],
  update_to_borrower_id:       ['BorrowerProfile', 'getApplication'],
};

// Tag that triggers the deferred (background) composite refetch
export const DEFERRED_COMPOSITE_TAG = 'BorrowerProfile';

// Extract tags split into immediate (section-level) and deferred (composite) groups
export function getInvalidationTagsFromFunctionCalls(functionCalls) {
  if (!Array.isArray(functionCalls) || functionCalls.length === 0) return { immediate: [], deferred: [] };
  const immediateSet = new Set();
  let needsComposite = false;
  for (const fc of functionCalls) {
    const tags = AI_TOOL_TAG_MAP[fc.name];
    if (tags) {
      tags.forEach((t) => {
        if (t === DEFERRED_COMPOSITE_TAG) {
          needsComposite = true;
        } else {
          immediateSet.add(t);
        }
      });
    }
  }
  return {
    immediate: [...immediateSet],
    deferred: needsComposite ? [DEFERRED_COMPOSITE_TAG] : [],
  };
}

// ── B2C ─────────────────────────────────────────────────────────────────────
// Maps B2C AI agent tool names to BorrowerScopedProfile section keys.
// These are used to build { type: 'BorrowerScopedProfile', id: `${borrowerId}-${section}` } tags.
export const B2C_TOOL_SECTION_MAP = {
  update_personal_details:     ['personal_details'],
  update_borrower_address:     ['personal_details'],
  update_loan_info:            ['personal_details'],
  update_employment_profiles:  ['employment_and_income'],
  update_employment_records:   ['employment_and_income'],
  update_borrower_assets:      ['user_asset_details'],
  update_borrower_liabilities: ['user_liabilities_details'],
  update_borrower_real_estate: ['user_reos_details'],
};

/**
 * Extract BorrowerScopedProfile invalidation tags for B2C from function calls.
 * Returns an array of { type, id } tag objects ready for dispatch.
 */
export function getB2CInvalidationTags(functionCalls, borrowerId) {
  if (!Array.isArray(functionCalls) || !functionCalls.length || !borrowerId) {
    return { immediate: [], deferred: false };
  }
  const sectionSet = new Set();
  for (const fc of functionCalls) {
    const sections = B2C_TOOL_SECTION_MAP[fc.name];
    if (sections) {
      sections.forEach((s) => sectionSet.add(s));
    }
  }
  const immediate = [...sectionSet].map((section) => ({
    type: 'BorrowerScopedProfile',
    id: `${borrowerId}-${section}`,
  }));
  return { immediate, deferred: sectionSet.size > 0 };
}
